import { task } from 'mobx-task'
import Workspace from 'iam/models/Workspace'
import { action, computed, makeObservable, observable } from 'mobx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { EditBundleParams } from 'admin/bundle/dtos/dtos'
import { BundleStore } from 'admin/bundle/BundleStore'
import WorkspaceSelectorViewModel from 'admin/bundle/WorkspaceSelectorForBundlesViewModel'
import InventoryStore from 'admin/InventoryStore'
import { RefreshBundles } from 'admin/bundle/BundleSearchViewModel'
import { mapIncludedItemVmToIds } from 'admin/bundle/dtos/bundleGrpcDtoMapper'
import { IWithWorkspaceSelectionForBundles } from 'admin/bundle/components/BundleFormWorkpaceDropdown'
import { required, validationContext } from 'validx'
import { Bundle } from 'admin/bundle/bundle'
import { Id } from 'types/definitions'
import ModalViewModel from 'infra/ModalViewModel'
import ManageItemsInBundleViewModel, {
  IncludedItemViewModel,
} from 'admin/bundle/components/ManageItemsInBundleViewModel/ManageItemsInBundleViewModel'
import { PriceEditorViewModel } from '../PriceEditorViewModel'
import { lengthBetween } from 'utils/validation-utils'

export default class EditBundleViewModel
  extends ModalViewModel
  implements IWithWorkspaceSelectionForBundles
{
  constructor(
    public inventoryItemStore: InventoryStore,
    private bundleStore: BundleStore,
    private refreshBundles: RefreshBundles,
    public getSelectedWorkspace: () => Workspace | undefined,
    public workspaceSelectorVM: WorkspaceSelectorViewModel,
    private newAlert: AlertFactory
  ) {
    super()
    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * The name for the Bundle.
   */
  @observable
  name = ''

  /**
   * The description for the Bundle.
   */
  @observable
  description = ''

  /**
   * The Bundle price.
   */
  @observable
  price = 0

  /**
   * The included items for the Bundle.
   */
  @observable
  includedItems: IncludedItemViewModel[] = []

  /**
   * The base inventory item id for the Bundle.
   */
  @observable
  baseItemId = ''

  /**
   * A selected Bundle. Used for edit/override operations.
   */
  @observable
  bundle: Bundle = Bundle.none

  /**
   * The selected workspace
   */
  @observable
  workspace? = Workspace.none

  manageItemsInBundleViewModel = new ManageItemsInBundleViewModel(
    this.setIncludedItems,
    this.inventoryItemStore
  )

  /**
   * The Bundle price editor view model.
   */
  readonly pricingVM = new PriceEditorViewModel()

  /**
   * Holds the maximum lengths for each field.
   */
  private readonly fieldLengths = {
    name: { min: 1, max: 100 },
    description: { min: 1, max: 280 },
  } as const

  /**
   * Validations for the workspace
   */
  workspaceValidation = validationContext(this, {
    workspace: [(opts) => !!opts.value || 'Workspace is required'],
  })

  /**
   * Validation context for this view model.
   */
  validation = validationContext(this, {
    name: [
      required('A name is required.'),
      lengthBetween(
        this.fieldLengths.name.min,
        this.fieldLengths.name.max,
        `Name must be between ${this.fieldLengths.name.min} and ${this.fieldLengths.name.max} characters.`
      ),
    ],
    description: [
      required('A description is required.'),
      lengthBetween(
        this.fieldLengths.description.min,
        this.fieldLengths.description.max,
        `Description must be between ${this.fieldLengths.description.min} and ${this.fieldLengths.description.max} characters.`
      ),
    ],
  })

  /**
   * Returns a sorted copy of the included items list, ordered by their names.
   *
   * @return {Array} The sorted copy of included items, ordered by name.
   */
  @computed
  get orderedIncludedItems(): IncludedItemViewModel[] {
    const baseItem = this.includedItems.find((i) => i.isBaseItem)
    const allButBaseItem = this.includedItems
      .filter((i) => !i.isBaseItem)
      .sort((a, b) => a.name.localeCompare(b.name))

    if (baseItem) {
      return [baseItem, ...allButBaseItem]
    }

    return allButBaseItem
  }

  /**
   * Computes the number of remaining characters for each field
   */
  @computed
  get remainingCharactersIndicator() {
    const makeIndicator = (max: number, current: number) =>
      `${max - current} characters remaining`

    return {
      name: makeIndicator(this.fieldLengths.name.max, this.name.length),
      description: makeIndicator(
        this.fieldLengths.description.max,
        this.description.length
      ),
    }
  }

  /**
   * Set the workspace for the new item
   */
  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
    this.manageItemsInBundleViewModel.setWorkspaceId(this.workspace?.id || '')
  }

  /**
   * Resets elements involved in the creation process
   */
  @action.bound
  reset() {
    this.name = ''
    this.description = ''
    this.price = 0
    this.includedItems = []
    this.baseItemId = ''
    this.manageItemsInBundleViewModel.baseItemId = ''
    this.manageItemsInBundleViewModel.includedItems = []

    this.submit.reset()
    this.validation.reset()
    this.pricingVM.reset()
  }

  @action.bound
  setBundle(bundle: Bundle) {
    this.bundle = bundle
    this.name = bundle.name
    this.description = bundle.description
    this.includedItems = bundle.includedItems.map(
      (i) =>
        new IncludedItemViewModel(
          true,
          i.isBaseItem,
          i.inventoryItemId,
          i.inventoryItemVersionId,
          i.name,
          i.price,
          i.proPrice,
          i.description,
          i.workspaceId,
          i.includedQuantity
        )
    )
    this.price = bundle.price
    this.baseItemId =
      this.includedItems.find((item) => item.isBaseItem)?.inventoryItemId || ''

    this.manageItemsInBundleViewModel.setBaseItemId(this.baseItemId)
    this.manageItemsInBundleViewModel.setCurrentlyIncludedItems(
      this.includedItems
    )

    this.pricingVM.hydrate(bundle.price)
  }

  @action
  open(bundle: Bundle) {
    this.reset()
    this.setWorkspace(bundle.workspace)
    this.setBundle(bundle)
    this.manageItemsInBundleViewModel.activate()
    this.show()
  }

  @action.bound
  setIncludedItems(includedItems: IncludedItemViewModel[]) {
    this.includedItems = includedItems
    this.baseItemId =
      includedItems.find((item) => item.isBaseItem)?.inventoryItemId || ''
    this.manageItemsInBundleViewModel.setBaseItemId(this.baseItemId)
    this.manageItemsInBundleViewModel.setCurrentlyIncludedItems(
      this.includedItems
    )
  }

  @action.bound
  validate(): boolean {
    // Both should run independently.
    const base = this.validation.reset().validate().isValid
    const pricing = this.pricingVM.validate()

    return base && pricing
  }

  /**
   * Edits the bundle
   */
  submit = task.resolved(async () => {
    if (
      !this.validate() ||
      !this.workspaceValidation.reset().validate().isValid
    ) {
      return
    }

    if (this.baseItemId === '') {
      this.newAlert()
        .withState(AlertState.Warning)
        .saying(
          'Base item is required',
          'Please mark the previously assigned base item or assign a different base item.'
        )
        .autoDismiss()
        .show()
      return
    }

    this.includedItems = this.includedItems.filter(
      (i) => !i.isBaseItem && i.includedQuantity > 0
    )

    const price = this.pricingVM.toValue()

    const payload: EditBundleParams = {
      name: this.name,
      description: this.description,
      price,
      baseItemId: this.baseItemId,
      includedItemsIds: mapIncludedItemVmToIds(
        this.includedItems.filter(
          (item) => item.inventoryItemId !== this.baseItemId
        )
      ),
    }

    const bundleId = Id.assert(this.bundle?.Id)

    await this.bundleStore.edit(bundleId, payload)

    // Refresh the list to show the edited bundle
    await this.refreshBundles(this.workspace || Workspace.none)

    this.validation.reset()

    this.newAlert()
      .withState(AlertState.Success)
      .saying('Success', 'Bundle edited.')
      .autoDismiss()
      .show()

    this.close()
  })
}
